import { computed, inject, Service, signal, Signal } from '@angular/core';
import { MgsWebApi } from '../mgs-webapi.service';
import { firstValueFrom } from 'rxjs';
import {
  IKidsBuchungDaten,
  IKidsPotentielleTerminInfo,
  IKidsReservierung,
  IKidsTermin,
  IKidsTerminFuerReservierung,
  IKidsTerminUebersicht,
  IMessages
} from '@mng/mgs-lib';
import { KidsHotelStore } from '../kids/kids-hotel/kids-hotel.store';
import { KidsCodetableStore } from '../kids/kids-codetable.store';
import { MngDialogService } from '@mng/mng-inputs';
import { differenceInDays } from 'date-fns';
import { KidsBuchungInfoService } from './kids-buchung-info/kids-buchung-info.service';
import { MngMessageService } from '@mng/mng-message-handler';
import { KidsResponsiveService } from './kids-responsive.service';
import { ensureNumberValue } from '@mng/mng-ts-helpers';
import { MatDialog } from '@angular/material/dialog';
import { MngGoogleMapsService } from '@mng/mng-base-ui';
import { IKidsHotelInfoParams, KidsHotelInfoComponent } from './kids-hotel/kids-hotel-info.component';
import { IKidsGeoMapParams, KidsGeoMapComponent } from './kids-geo-map/kids-geo-map.component';
import { AuthService } from '../auth/auth.service';

@Service()
export class KidsBuchungService {
  readonly dialog = inject(MatDialog);
  readonly dialogService = inject(MngDialogService);
  readonly messageService = inject(MngMessageService);
  readonly kidsBuchungInfoService = inject(KidsBuchungInfoService);
  readonly webApi = inject(MgsWebApi);
  readonly kidsHotelStore = inject(KidsHotelStore);
  readonly kidsCodetableStore = inject(KidsCodetableStore);
  readonly authService = inject(AuthService);
  readonly googleMapsService = inject(MngGoogleMapsService);

  readonly isDesktop = inject(KidsResponsiveService).isDesktop;

  buchungDaten = signal<IKidsBuchungDaten>(undefined);
  vn = computed(() => this.buchungDaten().vn);

  termineUebersicht: Signal<{ [kidsId: number]: IKidsTerminUebersicht[] }> = computed(() =>
    createTermineUebersicht(this.buchungDaten())
  );

  async login(code: string) {
    await firstValueFrom(this.webApi.kidsBuchungLogin(code));
    await this.authService.updateEnvironmentInfo();
    await this.ermittleKidsDaten();
  }

  async ermittleBuchungsDaten() {
    const result = await firstValueFrom(this.webApi.kidsBuchungDaten());
    this.buchungDaten.set(result.data);
  }

  async ermittleKidsDaten() {
    await Promise.all([this.kidsCodetableStore.load(), this.kidsHotelStore.load(), this.ermittleBuchungsDaten()]);
  }

  isTerminFristAbgelaufen(termin: IKidsTermin): boolean {
    const terminDate = new Date(termin.date);
    const today = new Date();
    const daysBetween = differenceInDays(terminDate, today);
    const result = daysBetween < this.buchungDaten().buchungFrist;
    return result;
  }

  async reservierungAufheben(kid: IKidsPotentielleTerminInfo) {
    if (this.isTerminFristAbgelaufen(kid.kidsTermin)) {
      this.kidsBuchungInfoService.terminFristAbgelaufen('Stornierung');
      return;
    }
    this.dialogService.showYesNo({ htmlContent: `Möchten Sie die Reservierung aufheben?` }, async () => {
      await firstValueFrom(this.webApi.kidsTerminReservierungAufheben({ kidDtos: [kid], mobile: !this.isDesktop() }));
      await this.ermittleBuchungsDaten();
    });
  }

  async wartelisteAufheben(kid: IKidsPotentielleTerminInfo, termin: IKidsTerminUebersicht) {
    this.dialogService.showYesNo(
      { htmlContent: `Möchten Sie die <strong>${kid.name}</strong> von der Warteliste entfernen?` },
      async () => {
        await firstValueFrom(
          this.webApi.kidsTerminWartelisteAufheben({
            kidDtos: [kid],
            datum: termin.date,
            hotelId: termin.hotelId,
            mobile: !this.isDesktop()
          })
        );
        void this.messageService.showMessage(`${kid.name} wurde von Warteliste entfernt!`);
        await this.ermittleBuchungsDaten();
      }
    );
  }

  async mapFuerHotelLocationDialog(hotelId: number) {
    await this.googleMapsService.init();
    const hotel = this.kidsHotelStore.entityMap()[hotelId];
    this.dialog.open(KidsGeoMapComponent, { minWidth: '96vw', data: { hotel } satisfies IKidsGeoMapParams });
  }

  async hotelInfoDialog(hotelId: number) {
    const hotel = this.kidsHotelStore.entityMap()[hotelId];
    this.dialog.open(KidsHotelInfoComponent, { data: { hotel } satisfies IKidsHotelInfoParams });
  }

  async aufWartelisteSetzenDurchfuehren(reservierung: IKidsReservierung) {
    const result: IMessages = await firstValueFrom(this.webApi.kidsTerminWarteliste(reservierung));
    if (result.messages?.length > 0) {
      this.dialogService.showOK({ htmlContent: result.messages[0].message });
    } else {
      void this.messageService.showMessage('Termin wurde auf die Warteliste gesetzt!');
    }

    await this.ermittleBuchungsDaten();
  }

  async reservierungDurchfuehren(reservierung: IKidsReservierung) {
    const result: IMessages = await firstValueFrom(this.webApi.kidsTerminReservierung(reservierung));

    if (result.messages?.length > 0) {
      this.dialogService.showOK({ htmlContent: result.messages[0].message });
    } else {
      void this.messageService.showMessage('Reservierung wurde erfolgreich durchgeführt');
    }

    await this.ermittleBuchungsDaten();
  }
}

/**
 *  Erzeugt ein Object und darin pro Kind ein Array von "Tagesterminen" -> "termineUebersicht"
 *  (d.h. komprimiert ohne Zeitinfo  etc.)
 *  Die ID des Kinds ist das Property
 *
 *  Pro Tagestermin gibt es dann noch ein "terminList" Array dass dann die "echten" freien, Termine beinhaltet. Aus diesem Array wird
 *  dann im Zuge der Reservierung ausgewählt
 *
 *  <code>
 *      {
 *        12225: {
 *           termineUebersicht: [
 *             {
 *                hotelBezeichnung: 'Hotel xyz',
 *                ...
 *                terminList: []
 *             }
 *           ]
 *        },
 *        12226: {
 *      }
 *      }
 *  </code>
 *
 */
export function createTermineUebersicht(buchungsDaten: IKidsBuchungDaten): {
  [kidsId: number]: IKidsTerminUebersicht[];
} {
  // Es wird zu dem Object auch ein Array gehalten da Angular filter und sort nur mit Arrays arbeiten

  const kidsTermineObject: { [kidsId: number]: IKidsTerminUebersicht[] } = {};

  for (const kid of buchungsDaten.kids) {
    const kidTerminMap = new Map<string, IKidsTerminUebersicht>();

    if (!kid.kidsTermin) {
      for (const termin of kid.kidsTermine) {
        addToTermineUebersichtObject(kidTerminMap, termin);
      }
      const termine: IKidsTerminUebersicht[] = Array.from(kidTerminMap.values());
      termine.forEach((terminUebersicht) => {
        terminUebersicht.reservierungMoeglich = terminUebersicht.anzahlFrei > 0;
        terminUebersicht.infoText = terminUebersicht.reservierungMoeglich
          ? `${terminUebersicht.anzahlFrei} frei`
          : terminUebersicht.aufWarteListe
            ? 'Auf Warteliste'
            : 'Warteliste';
      });

      kidsTermineObject[kid.id] = termine;
    }
  }

  return kidsTermineObject;
}

/**
 * Befüllt Objekt mit Termindaten: 1. Ebene -> ID des Kindes darunter pro Hotelbezeichung+Termindatum
 */
function addToTermineUebersichtObject(kidTerminMap: Map<string, IKidsTerminUebersicht>, termin: IKidsTermin) {
  const key = termin.hotelId + '_' + termin.date;

  let terminUebersicht: IKidsTerminUebersicht;

  if (!kidTerminMap.has(key)) {
    terminUebersicht = {
      key,
      date: termin.date,
      hotelId: termin.hotelId,
      hotelBezeichnung: termin.hotelBezeichnung,
      hotelBundesland: termin.hotelBundesland,
      anzahlFrei: 0,
      terminList: [],
      aufWarteListe: false
    };
    kidTerminMap.set(key, terminUebersicht);
  } else {
    terminUebersicht = kidTerminMap.get(key);
  }

  const anzahlFrei = ensureNumberValue(termin.anzahlPersonen) - ensureNumberValue(termin.anzahlGebucht);

  // nur Termine mit freien Plätzen der "terminList" hinzufuegen
  if (anzahlFrei > 0) {
    const terminFuerReservierung = { ...termin, anzahlFrei } satisfies IKidsTerminFuerReservierung;
    terminUebersicht.terminList.push(terminFuerReservierung);
  }

  if (termin.warteListeId) {
    terminUebersicht.aufWarteListe = true;
  }

  terminUebersicht.anzahlFrei += anzahlFrei;
}
